import {
  Ban,
  Check,
  CircleAlert,
  DoorOpen,
  Inbox,
  ShoppingBag,
  Store,
  ThumbsUp,
  X,
  type LucideIcon,
} from 'lucide-react';

import type { DeliveryJob } from '@/api/types';
import { formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The whole run, start to end — not just what has happened.
 *
 * The same idea as the customer app's order timeline, seen from the other
 * side: the customer asks "how far along is my medicine", the rider asks "what
 * have I already told the app". Both are answered by drawing every step from
 * the outset — the ones behind carrying their timestamp, the current one
 * highlighted, the ones ahead greyed rather than hidden.
 *
 * Steps are marked done by having a TIMESTAMP, never by comparing against the
 * current status. A delivery that jumped a state — OUT_FOR_DELIVERY straight
 * to DELIVERED, which the state machine allows for a rider who never tapped
 * "at the door" — would otherwise show a gap it never had.
 */

type StepState = 'done' | 'current' | 'upcoming' | 'failed';

type Step = {
  label: string;
  icon: LucideIcon;
  state: StepState;
  at?: string | null;
  detail?: string | null;
};

function buildDeliverySteps(job: DeliveryJob): Step[] {
  const steps: Step[] = [
    // `assignedAt` can be null on a job assigned before the column existed,
    // and the step is true regardless — it is on this rider's sheet.
    { label: 'Assigned to you', icon: Inbox, at: job.assignedAt, state: 'done' },
    { label: 'Accepted', icon: ThumbsUp, at: job.acceptedAt, state: stateOf(job.acceptedAt) },
    {
      label: `At ${job.pharmacyName ?? 'the pharmacy'}`,
      icon: Store,
      at: job.arrivedAtPharmacyAt,
      state: stateOf(job.arrivedAtPharmacyAt),
    },
    { label: 'Bag collected', icon: ShoppingBag, at: job.pickedUpAt, state: stateOf(job.pickedUpAt) },
    {
      label: 'At the door',
      icon: DoorOpen,
      at: job.arrivedAtCustomerAt,
      state: stateOf(job.arrivedAtCustomerAt),
    },
    { label: 'Handed over', icon: Check, at: job.deliveredAt, state: stateOf(job.deliveredAt) },
  ];

  /*
   * A failure TRUNCATES the run rather than sitting inside it.
   *
   * Drawing "handed over" greyed under "could not deliver" would suggest the
   * job is still going somewhere. Everything not actually reached is dropped,
   * and the reason takes its place.
   */
  if (job.status === 'FAILED' || job.status === 'CANCELLED') {
    const reached = steps.filter((step) => step.state === 'done');
    reached.push({
      label: job.status === 'CANCELLED' ? 'Cancelled' : 'Could not deliver',
      icon: job.status === 'CANCELLED' ? Ban : CircleAlert,
      state: 'failed',
      detail: job.failureReason,
    });
    return reached;
  }

  /*
   * The first step still outstanding is what is being done right now. Derived
   * from the steps rather than from `job.status` so the two can never disagree
   * on screen — the highlight always sits directly after the last thing that
   * actually happened.
   */
  const next = steps.findIndex((step) => step.state === 'upcoming');
  if (next !== -1) steps[next] = { ...steps[next]!, state: 'current' };

  return steps;
}

function stateOf(at: string | null | undefined): StepState {
  return at ? 'done' : 'upcoming';
}

export function DeliveryTimeline({ job }: { job: DeliveryJob }) {
  const steps = buildDeliverySteps(job);

  return (
    <ol className="grid">
      {steps.map((step, index) => (
        <StepRow
          key={`${step.label}-${index}`}
          step={step}
          isFirst={index === 0}
          isLast={index === steps.length - 1}
          /*
           * The line ABOVE a step belongs to the step before it: the track is
           * solid up to what has happened and faint beyond, so the fill is
           * itself a progress bar.
           */
          filledAbove={index > 0 && steps[index - 1]!.state !== 'upcoming'}
        />
      ))}
    </ol>
  );
}

function StepRow({
  step,
  isFirst,
  isLast,
  filledAbove,
}: {
  step: Step;
  isFirst: boolean;
  isLast: boolean;
  filledAbove: boolean;
}) {
  const done = step.state === 'done';
  const current = step.state === 'current';
  const failed = step.state === 'failed';

  return (
    <li className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3">
      {/* The track. */}
      <div className="grid grid-rows-[0.875rem_auto_1fr] justify-items-center">
        <span
          aria-hidden="true"
          className={cn(
            'w-0.5 rounded-full',
            isFirst ? 'bg-transparent' : filledAbove ? 'bg-primary' : 'bg-muted',
          )}
        />
        <Marker step={step} />
        <span
          aria-hidden="true"
          className={cn('w-0.5 rounded-full', isLast ? 'bg-transparent' : done ? 'bg-primary' : 'bg-muted')}
        />
      </div>

      <div className={cn('pt-1.5', isLast ? 'pb-1' : 'pb-4')}>
        <div className="flex items-baseline justify-between gap-3">
          <p
            className={cn(
              'min-w-0 text-[0.9375rem] leading-snug [overflow-wrap:anywhere]',
              current || failed ? 'font-semibold' : 'font-normal',
              step.state === 'upcoming'
                ? 'text-ink-subtle'
                : failed
                  ? 'text-error-ink'
                  : 'text-ink',
            )}
          >
            {step.label}
            {/* The state in words, for anyone who cannot see the track. */}
            {current ? <span className="sr-only-text"> — happening now</span> : null}
            {step.state === 'upcoming' ? <span className="sr-only-text"> — not yet</span> : null}
          </p>
          {step.at ? (
            <time
              data-numeric
              dateTime={step.at}
              className="shrink-0 text-xs text-ink-subtle"
            >
              {formatTime(step.at)}
            </time>
          ) : null}
        </div>

        {step.detail ? (
          <p className="mt-0.5 text-sm leading-snug text-ink-muted [overflow-wrap:anywhere]">
            {step.detail}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function Marker({ step }: { step: Step }) {
  const base = 'grid size-5 shrink-0 place-items-center rounded-full forced-outline';

  if (step.state === 'done') {
    return (
      <span className={cn(base, 'bg-primary text-on-primary')} aria-hidden="true">
        <Check className="size-3" strokeWidth={3} />
      </span>
    );
  }

  if (step.state === 'failed') {
    return (
      <span className={cn(base, 'bg-danger text-on-danger')} aria-hidden="true">
        <X className="size-3" strokeWidth={3} />
      </span>
    );
  }

  // A ring rather than a filled dot, so the step being worked on reads as
  // OPEN — the one place on the track where something is still happening.
  if (step.state === 'current') {
    return (
      <span
        aria-hidden="true"
        className={cn(base, 'border-[3px] border-primary bg-raised')}
      />
    );
  }

  return (
    <span className={cn(base)} aria-hidden="true">
      <span className="size-2 rounded-full bg-muted" />
    </span>
  );
}
